import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import DocumentEditor from './components/DocumentEditor';
import VersionHistory from './components/VersionHistory';
import PublishControls from './components/PublishControls';

const DEFAULT_CONTENT = {
  'privacy-policy': `PRIVACY POLICY

Last Updated: January 1, 2025 | Version 1.0

1. INTRODUCTION

ConstructionPro ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.

2. INFORMATION WE COLLECT

We collect information you provide directly to us, including:
- Account Information: Name, email address, job title, and password when you register.
- Profile Data: Profile photo, company affiliation, and professional details.
- Project Data: Construction project details, bids, resources, and related business information.
- Usage Data: Log data, IP addresses, browser type, pages visited, and time spent on the platform.

3. HOW WE USE YOUR INFORMATION

We use the information we collect to:
- Provide, operate, and maintain the ConstructionPro platform
- Process transactions and send related information
- Send administrative information, updates, and security alerts
- Monitor and analyze usage patterns to improve the platform
- Detect, prevent, and address technical issues and fraudulent activity
- Comply with legal obligations

4. DATA RETENTION

We retain your personal data for as long as your account is active. Upon account closure, personal data is deleted within 90 days, except where required for legal purposes (up to 7 years for financial records).

5. CONTACT US

Email: privacy@constructionpro.com
Address: ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001`,

  'legal-terms': `LEGAL TERMS

Effective Date: January 1, 2025 | Version 1.0

1. AGREEMENT TO TERMS

By accessing or using the ConstructionPro platform, you agree to be bound by these Legal Terms and all applicable laws and regulations.

2. USE LICENSE

Subject to your compliance with these Terms, ConstructionPro grants you a limited, non-exclusive, non-transferable, revocable license to access and use the platform for your internal business purposes.

You may not:
- Modify, copy, or create derivative works based on the platform
- Reverse engineer or attempt to extract source code
- Sell, resell, or sublicense access to the platform
- Use the platform for any unlawful purpose

3. LIMITATION OF LIABILITY

To the maximum extent permitted by law, ConstructionPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages. Total liability shall not exceed $100 USD or amounts paid in the preceding 12 months.

4. GOVERNING LAW

These Terms shall be governed by the laws of the State of New York.

5. CONTACT US

Email: legal@constructionpro.com
Address: ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001`,

  'cookie-policy': `COOKIE POLICY

Last Updated: January 1, 2025 | Version 1.0

1. WHAT ARE COOKIES

Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently and provide information to site owners.

2. COOKIES WE USE

Essential Cookies (Required)
- sb-access-token: Authentication session token (Session)
- sb-refresh-token: Session refresh and renewal (1 week)
- csrf_token: Cross-site request forgery protection (Session)

Functional Cookies
- user_preferences: Stores UI preferences and layout settings (1 year)
- table_config: Remembers table column and sort preferences (6 months)

Analytics Cookies
- _ga: Google Analytics - Distinguishes unique users (2 years)
- _gid: Google Analytics - Distinguishes users within 24 hours (24 hours)

Marketing Cookies
- _fbp: Meta - Tracks visits for advertising purposes (3 months)

3. HOW TO CONTROL COOKIES

You can manage cookie preferences through our Cookie Preference Center at /cookie-policy or through your browser settings.

4. CONTACT US

Email: privacy@constructionpro.com
Address: ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001`,
};

const MOCK_VERSIONS = [
  { id: 1, version: '1.0', summary: 'Initial publication', author: 'Admin', date: 'Jan 1, 2025, 09:00 AM' },
];

const TABS = [
  { id: 'privacy-policy', label: 'Privacy Policy', icon: 'Shield', path: '/privacy-policy' },
  { id: 'legal-terms', label: 'Legal Terms', icon: 'FileText', path: '/legal-terms' },
  { id: 'cookie-policy', label: 'Cookie Policy', icon: 'Cookie', path: '/cookie-policy' },
];

const LegalContentManagement = () => {
  const [activeTab, setActiveTab] = useState('privacy-policy');
  const [contents, setContents] = useState(DEFAULT_CONTENT);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [publishedNotice, setPublishedNotice] = useState(false);
  const [activePanel, setActivePanel] = useState('editor'); // 'editor' | 'history'

  const handleContentChange = (value) => {
    setContents((prev) => ({ ...prev, [activeTab]: value }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 800));
    setSaving(false);
    const now = new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setLastSaved(now);
  };

  const handlePublish = async () => {
    setPublishing(true);
    await new Promise((r) => setTimeout(r, 1200));
    setPublishing(false);
    setPublishedNotice(true);
    const now = new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setLastSaved(now);
    setTimeout(() => setPublishedNotice(false), 4000);
  };

  const activeTabData = TABS?.find((t) => t?.id === activeTab);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Legal Content Management</h1>
            <p className="text-gray-600 mt-1">Update and publish Privacy Policy, Legal Terms, and Cookie Policy documents.</p>
          </div>
          {publishedNotice && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <Icon name="CheckCircle" size={16} />
              Document published successfully!
            </div>
          )}
        </div>

        {/* Document Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 px-4 pt-4">
            <div className="flex gap-1 overflow-x-auto">
              {TABS?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => { setActiveTab(tab?.id); setLastSaved(null); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-accent text-accent bg-accent/5' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Sub-panel toggle */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex gap-2">
                <button
                  onClick={() => setActivePanel('editor')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activePanel === 'editor' ?'bg-gray-900 text-white' :'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon name="Edit3" size={14} />
                  Editor
                </button>
                <button
                  onClick={() => setActivePanel('history')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activePanel === 'history' ?'bg-gray-900 text-white' :'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon name="Clock" size={14} />
                  Version History
                </button>
              </div>
              <a
                href={activeTabData?.path}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-accent hover:underline"
              >
                <Icon name="ExternalLink" size={13} />
                View Live Page
              </a>
            </div>

            {/* Main content area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {activePanel === 'editor' ? (
                  <DocumentEditor
                    content={contents?.[activeTab] || ''}
                    onChange={handleContentChange}
                    placeholder={`Enter ${activeTabData?.label} content here...`}
                  />
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-accent" />
                      Version History — {activeTabData?.label}
                    </h3>
                    <VersionHistory
                      versions={MOCK_VERSIONS}
                      onRestore={(v) => alert(`Restoring version ${v?.version}`)}
                    />
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                <PublishControls
                  onSaveDraft={handleSaveDraft}
                  onPublish={handlePublish}
                  saving={saving}
                  publishing={publishing}
                  lastSaved={lastSaved}
                />

                {/* Document Info */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                    <Icon name="Info" size={15} className="text-accent" />
                    Document Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current Version</span>
                      <span className="font-medium text-gray-900">1.0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Published</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last Updated</span>
                      <span className="font-medium text-gray-900">Jan 1, 2025</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Characters</span>
                      <span className="font-medium text-gray-900">{(contents?.[activeTab] || '')?.length?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
                    <Icon name="Link" size={15} className="text-accent" />
                    All Legal Pages
                  </h3>
                  <div className="space-y-2">
                    {TABS?.map((tab) => (
                      <a
                        key={tab?.id}
                        href={tab?.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Icon name={tab?.icon} size={14} />
                          {tab?.label}
                        </div>
                        <Icon name="ExternalLink" size={12} className="text-gray-400" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default LegalContentManagement;
